<template>
    <div
        class="navbar gpu floater fixed w-full z-50 transition-all duration-200 top-0"
        :class="{
            'navbar--top--visible': !shadowMenu,
            'bg-black-900 mobile:overflow-y-auto': showMobileMenu && isMobileWidth }">
        <div
            class="menu menu-wrapper mobile:block"
            :class="[
                classes.menu.mobile,
                classes.menu.tabletLandscape,
                {'mobile:h-screen' : showMobileMenu},
            ]"
        >
            <!-- TIS logo and Hamburger Button Wrapper-->
            <div class="logo-and-hamburger flex flex-row justify-between">
                <div>
                    <TisHeaderLogo :shadow="shadowMenu"/>
                </div>
                <div
                    v-show="!showMobileMenu"
                    class="hamburger"
                    :class="
                        [
                            classes.hamburger_button.mobile,
                            classes.hamburger_button.tabletLandscape
                        ]"
                    @click="showMobileMenu = true"
                >
                    <svg-icon
                        icon="menuBars"
                        class="bg-black-900 text-gold-500 w-50px h-50px px-3 py-4 transition-all "
                    />
                    <div class="bg-black-40 p-4">
                        Menu
                    </div>
                </div>
                <div
                    class="close-menu-button transition-all absolute cursor-pointer top-6 right-6 z-50"
                    :class="{'opacity-0': !showMobileMenu || !isMobileWidth}"
                    @click="handleCloseMobileMenu"
                >
                    <svg-icon
                        icon="close"
                        class="text-gold-500 w-6 h-6  "
                    />
                </div>
            </div>

            <!--Menu List -->
            <ul
                v-show="showMobileMenu || !isMobileWidth"
                class="menu-list flex flex-col relative"
                :class="
                    [
                        classes.menu_list.mobile,
                        classes.menu_list.tabletLandscape,
                        {
                            'bg-black-900 opacity-90 tabletLandscape:border-t-2 tabletLandscape:border-gold-500 tabletLandscape:rounded': hoverMenu || showPanel || (showMobileMenu && isMobileWidth || shadowMenu),
                            'shadow-2xl': shadowMenu,
                        }
                    ]"
                @mouseover="hoverMenu = true"
                @mouseleave="hoverMenu = false"
            >
                <li
                    v-show="isMobileWidth"
                    class="mobile:mb-6 ml-4">
                    <tis-button
                        :url="route('web.home')"
                        class="btn-flat-menu text-white"
                        v-text="menu.homeLabel"/>
                </li>
                <li
                    class="mobile:mb-6 mobile:ml-4"
                    @click="toggleMenuExpansionPanel">
                    <tis-button
                        v-text="menu.servicesLabel"
                        class="btn-flat-menu text-white"
                        :url="route('web.services.post-production.home')"
                    />
                </li>
                <li class="about-us-link mobile:mb-6 ml-4">
                    <tis-button
                        :url="route('web.about')"
                        class="btn-flat-menu text-white"
                        v-text="menu.aboutLabel"/>
                </li>
                <li class="mobile:mb-6 ml-4">
                    <tis-button
                        :url="route('web.contact')"
                        class="btn-flat-menu text-white"
                        v-text="menu.contactLabel"/>
                </li>
                <li class="mobile:mb-6 ml-4">
                    <a
                        href="https://help.imagesalon.com"
                        target="_blank"
                        class="btn-flat-menu text-white"
                        v-text="menu.supportLabel"
                    />
                </li>
                <li class="mobile:mb-6 ml-4">
                    <tis-button
                        url="https://imagesalon.blog"
                        class="btn-flat-menu text-white"
                        v-text="menu.blog"
                    />
                </li>
                <li class="mobile:mb-6 mobile:ml-4 flex justify-start tabletLandscape:ml-4">
                    <tis-button
                        :url="route('web.registration')"
                        :full-redirect="true"
                        class="btn-gold-tight text-sm py-2 rounded-md"
                        v-text="menu.getStartedLabel"
                    />
                </li>
                <li
                    v-if="menu.clientLoginLabel"
                    class="mobile:mb-10 ml-4 flex justify-start">
                    <tis-icon-topped-wrapper
                        icon="checkmark"
                        :url="route('web.login')"
                        :label="menu.clientLoginLabel"
                        :full-redirect="true"
                    />
                </li>
                <li
                    v-if="menu.clientLogoutLabel"
                    class="mobile:mb-10 ml-4">
                    <tis-button
                        :url="route('web.logout')"
                        class="btn-flat-menu text-white"
                        :full-redirect="true"
                        v-text="menu.clientLogoutLabel"
                    />
                </li>
                <li class="mobile:w-12 ml-4 cursor-pointer">
                    <tis-button
                        :url="locale.switchLocaleUrl"
                        class="language-switcher pa-6 flex content-between items-center h-8 mobile:pt-12 mobile:border-t border-gray-700 tabletLandscape:border-l tabletLandscape:pl-4"
                        :full-redirect="true"
                    >
                        <svg-icon
                            :icon="currentLanguage"
                            class="w-6 h-4"/>
                        <div
                            class="uppercase text-white ml-2"
                            :text="languageLabel"/>
                    </tis-button>
                </li>
            </ul>
            <ul
                v-show="showMobileMenu || !isMobileWidth"
                class="mobile-contact-information tabletLandscape:hidden leading-loose text-gray-700 uppercase text-sm font-bold tracking-wide flex-row my-8 ml-12">
                <li>
                    <tis-button
                        :url="contact.tollFreePhone | telHref"
                        class="btn-flat-footer-gold"
                        v-text="contact.tollFreePhone"/>
                </li>
                <li>
                    <tis-button
                        :url="'mailto:' + contact.email"
                        class="btn-flat-footer-gold"
                        v-text="contact.email"/>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import TisButton from '@whisper/components/buttons/TisButton';
import TisIconToppedWrapper from '@whisper/components/buttons/TisIconToppedWrapper';
import SvgIcon from '@whisper/components/icons/SvgIcon';
import MenuExpansionPanel from '@web/components/MenuExpansionPanel';
import vClickOutside from 'v-click-outside';
import { telHref } from '@whisper/filters/telHref';
import TisHeaderLogo from '@web/components/TisHeaderLogo';

export default {
    name: 'TisMenu',
    components: {
        TisHeaderLogo,
        TisButton,
        SvgIcon,
        MenuExpansionPanel,
        TisIconToppedWrapper,
    },
    directives: {
        clickOutside: vClickOutside.directive,
    },
    filters: {
        telHref,
    },
    props: {
        servicesCategories: {
            type: Object,
            required: true,
        },
        contact: {
            type: Object,
            required: true,
        },
        locale: {
            type: Object,
            required: true,
            default: () => ({
                currentLanguage: 'en',
                switchLocaleUrl: `${document.URL}?locale=fr`,
            }),
        },
        menu: {
            type: Object,
            required: true,
            default: () => ({
                servicesLabel: 'Services',
                aboutLabel: 'About Us',
                contactLabel: 'Contact Us',
                blog: 'Blog',
                getStartedLabel: 'Get Started',
                clientLoginLabel: 'Client Log-In',
                clientLogoutLabel: '',
                getStartedIconTop: 'checkmark',
            }),
        },
    },
    data: () => ({
        window: {
            width: 0,
            height: 0,
        },
        lastScrollPosition: 0,
        showMobileMenu: false,
        showPanel: false,
        hoverMenu: false,
        shadowMenu: false,
        classes: {
            menu: {
                mobile: [
                    'flex',
                    'flex-col',
                    'p-3',
                    'mobile:pt-6',
                ],
                tabletLandscape: [
                    'tablet:px-12',
                    'tablet:p-6',
                    'tabletLandscape:flex-row',
                    'tabletLandscape:items-center',
                    'tabletLandscape:justify-between',
                    'transition-all',
                ],

            },
            hamburger_button: {
                mobile: [
                    'uppercase',
                    'text-white', 'flex', 'justify-center', 'items-center', 'transition-all',
                    'hover:scale-95',
                ],
                tabletLandscape: ['tabletLandscape:hidden'],
            },
            menu_list: {
                mobile: [
                    'mobile:pt-10',
                    'mobile:pl-8',
                ],
                tabletLandscape: [
                    'tabletLandscape:p-4',
                    'tabletLandscape:flex',
                    'tabletLandscape:flex-row',
                    'tabletLandscape:items-center',
                    'tabletLandscape:border-t-2',
                    'tabletLandscape:border-transparent',
                ],
            },
        },
    }),
    computed: {
        isMobileWidth() {
            if (this.window.width < 1024) {
                return true;
            }
            return false;
        },
        languageLabel() {
            if (this.locale.currentLanguage === 'fr') {
                return 'EN';
            }

            return 'FR';
        },
        currentLanguage() {
            if (this.locale.currentLanguage === 'fr') {
                return 'flag-en';
            }

            return 'flag-fr';
        },
    },
    created() {
        window.addEventListener('resize', this.handleResize);
        this.handleResize();
    },
    destroyed() {
        window.removeEventListener('resize', this.handleResize);
    },
    mounted() {
        window.addEventListener('scroll', this.onScroll);
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.onScroll);
    },
    methods: {
        handleResize() {
            this.window.width = window.innerWidth;
            this.window.height = window.innerHeight;
        },
        handleCloseMobileMenu() {
            this.showMobileMenu = false;
            this.showPanel = false;
        },
        handleClosePanel() {
            this.showPanel = false;
        },
        onScroll() {
            const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;
            // Because of momentum scrolling on mobiles, we shouldn't continue if it is less than zero
            if (currentScrollPosition < 0) {
                return;
            }

            // Do not hide the open mobile menu when scrolling
            if (this.isMobileWidth && this.showMobileMenu) {
                return;
            }

            if (currentScrollPosition === 0 || currentScrollPosition < this.lastScrollPosition) {
                // this gives enough time for the snap scroll to happen
                this.showNavBar(currentScrollPosition);
            } else {
                this.hideNavBar(currentScrollPosition);
            }
        },
        showNavBar(currentScrollPosition) {
            this.shadowMenu = currentScrollPosition > 50;
            this.lastScrollPosition = currentScrollPosition;
            this.showPanel = currentScrollPosition !== this.lastScrollPosition;
        },
        hideNavBar(currentScrollPosition) {
            this.shadowMenu = true;
            this.lastScrollPosition = currentScrollPosition;
            this.showPanel = false;
        },
        toggleMenuExpansionPanel() {
            this.showPanel = !this.showPanel;
        },
    },
};
</script>

<style scoped>
.navbar.navbar--hidden {
    transform: translate3d(0, -100%, 0);
}
</style>
